import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';

export const Cart: React.FC = () => {
  const { items, removeFromCart, total } = useCart();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <Box
      sx={{
        py: 4,
        minHeight: '100vh',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 1000,
          margin: '0 auto',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <ShoppingCartIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Mon Panier
            </Typography>
          </Box>

          {items.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Votre panier est vide
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Découvrez notre collection de cartes de lecture
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/store')}
                sx={{
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontWeight: 'bold',
                }}
              >
                Voir les produits
              </Button>
            </Paper>
          ) : (
            <>
              <List sx={{ mb: 4 }}>
                {items.map((item) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      sx={{
                        py: 2,
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={'/assets/img/card.png'}
                          alt={item.name}
                          sx={{
                            width: 80,
                            height: 80,
                            mr: 2,
                            borderRadius: 2,
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                          }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                icon={<LocalShippingIcon />}
                                label="Livraison gratuite"
                                size="small"
                                color="success"
                                sx={{ mr: 1 }}
                              />
                              <Chip
                                icon={<PaymentIcon />}
                                label="Paiement sécurisé"
                                size="small"
                                color="primary"
                              />
                            </Box>
                          </Box>
                        }
                      />
                      <Box sx={{ textAlign: 'right', mr: 4 }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {item.price}€
                        </Typography>
                      </Box>
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => removeFromCart(item.id)}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              bgcolor: theme.palette.error.light + '20',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: theme.palette.primary.main + '10',
                  border: `1px solid ${theme.palette.primary.main}30`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                    {total}€
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleCheckout}
                  sx={{
                    borderRadius: '50px',
                    py: 1.5,
                    fontWeight: 'bold',
                    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
                  }}
                >
                  Procéder au paiement
                </Button>
              </Paper>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
